import { useState, useEffect } from 'react';
import { VoucherBook, useVoucherBooks, useDeleteVoucherBook } from '../hooks/useVoucherBooksApi';
import { VoucherBookFormModal } from './VoucherBookFormModal';
import { Loader2, Plus, ArrowUpRight, Pencil, Trash2, BookOpen, Download, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';

const FINANCIAL_YEARS = ['FY 2026-2027', 'FY 2025-2026', 'FY 2024-2025'];

const TYPE_BADGE_COLORS: Record<string, string> = {
  'Contra':      'bg-blue-50 text-blue-700 border-blue-200',
  'Journal':     'bg-purple-50 text-purple-700 border-purple-200',
  'Sales':       'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Purchase':    'bg-orange-50 text-orange-700 border-orange-200',
  'Credit Note': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Debit Note':  'bg-red-50 text-red-700 border-red-200',
  'Receipt':     'bg-green-50 text-green-700 border-green-200',
  'Payment':     'bg-amber-50 text-amber-700 border-amber-200',
};

export function VoucherBooksList() {
  const [financialYear, setFinancialYear] = useState('FY 2026-2027');
  const { data, isLoading, isError, refetch } = useVoucherBooks({ financialYear });
  const deleteBook = useDeleteVoucherBook();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<VoucherBook | null>(null);

  const books: VoucherBook[] = data || [];

  // Force a fresh fetch whenever this tab is opened
  useEffect(() => { refetch(); }, [financialYear]);

  const handleEdit = (book: VoucherBook) => { setEditingBook(book); setIsModalOpen(true); };
  const handleCreateNew = () => { setEditingBook(null); setIsModalOpen(true); };
  const handleDelete = (id: string) => {
    if (confirm('Delete this voucher book?')) deleteBook.mutate(id);
  };

  const handleDownloadCSV = () => {
    const rows = [
      ['#', 'Voucher Book', 'Type', 'Entries', 'Reversed', 'Last Entry Date'],
      ...books.map((b, i) => [
        i + 1,
        b.name,
        b.type,
        b.numberOfEntries,
        b.numberOfReversedEntries,
        b.lastEntryDate ? new Date(b.lastEntryDate).toLocaleDateString('en-IN') : '-',
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voucher-books-${financialYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <BookOpen className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Voucher Books ✨</h2>
            <p className="text-sm text-muted-foreground">Manage templates and sequences for accounting vouchers</p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground font-medium mb-1">Select Financial Year</label>
            <select
              className="border border-input rounded-md text-sm px-3 py-1.5 bg-background"
              value={financialYear}
              onChange={e => setFinancialYear(e.target.value)}
            >
              {FINANCIAL_YEARS.map(fy => <option key={fy}>{fy}</option>)}
            </select>
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={handleDownloadCSV} disabled={books.length === 0}>
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={handleCreateNew} className="h-9 bg-pink-600 hover:bg-pink-700">
            <Plus className="w-4 h-4 mr-1" /> New Voucher Book
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3 text-red-500">
          <p>Failed to load voucher books.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden shadow-sm bg-white dark:bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-muted/50 border-b border-border text-muted-foreground font-semibold text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3 w-8">#</th>
                  <th className="px-5 py-3">Voucher Books</th>
                  <th className="px-5 py-3">Voucher Book Type</th>
                  <th className="px-5 py-3 text-center">Number of Entries ↓</th>
                  <th className="px-5 py-3 text-center">Number of Reversed Entries</th>
                  <th className="px-5 py-3">Last Entry Date</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {books.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-muted-foreground">
                      No voucher books found. Click "+ New Voucher Book" to create one.
                    </td>
                  </tr>
                ) : (
                  books.map((book, idx) => (
                    <tr key={book._id} className="hover:bg-muted/30 transition-colors group">
                      <td className="px-5 py-3 text-muted-foreground text-xs">{idx + 1}</td>
                      <td className="px-5 py-3 font-medium text-foreground">{book.name}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${TYPE_BADGE_COLORS[book.type] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                          {book.type}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-center font-mono font-medium">{book.numberOfEntries}</td>
                      <td className="px-5 py-3 text-center font-mono">{book.numberOfReversedEntries}</td>
                      <td className="px-5 py-3 text-muted-foreground">
                        {book.lastEntryDate ? new Date(book.lastEntryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            title="Open"
                            className="flex flex-col items-center text-[10px] text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                          >
                            <ArrowUpRight className="w-4 h-4 mb-0.5" />
                            Open
                          </button>
                          <button
                            title="Edit"
                            onClick={() => handleEdit(book)}
                            className="flex flex-col items-center text-[10px] text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-50"
                          >
                            <Pencil className="w-4 h-4 mb-0.5" />
                            Edit
                          </button>
                          <button
                            title="New Entry"
                            className="flex flex-col items-center text-[10px] text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50"
                          >
                            <Plus className="w-4 h-4 mb-0.5" />
                            New Entry
                          </button>
                          <button
                            title="Delete"
                            onClick={() => handleDelete(book._id)}
                            className="flex flex-col items-center text-[10px] text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mb-0.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {books.length > 0 && (
            <div className="px-5 py-3 bg-muted/30 border-t border-border text-xs text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{books.length}</span> voucher books for <span className="font-semibold text-foreground">{financialYear}</span>
            </div>
          )}
        </div>
      )}

      <VoucherBookFormModal
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) setEditingBook(null);
        }}
        editBook={editingBook}
      />
    </div>
  );
}
